import { useState, useRef } from 'react';
import { typography, semantic, shadow, radius } from '@/shared/theme/tokens';
import { useApiErrorToast, useToast } from '@/shared/ui/Toast';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { UnsavedBar } from '@/shared/ui/UnsavedBar';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useParameters } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { useOverviewConfig, useSaveOverviewConfig } from '@/features/deploymentSettings/hooks/useOverviewConfig';
import { useSvgTemplates } from '@/features/overviewDiagram/hooks/useSvgTemplates';
import { SvgDiagram } from '@/features/overviewDiagram/ui/SvgDiagram';
import { parseBulkSvgSections, buildSampleCsv } from '@/features/overviewDiagram/ui/bulkSvgSections';
import { BulkUploadModal } from '@/shared/ui/BulkUploadModal';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { SearchInput } from '@/shared/ui/SearchInput';
import { AppSelect } from '@/shared/ui/AppSelect';

// Extracted from ShiftsTab: the deployment Overview-page configuration UI
// (critical params, dashboard sections, and SVG/HMI diagram region binding).

function buildInitialOverview(config) {
  return {
    criticalParameterIds: config?.criticalParameterIds ?? [],
    sections: config?.sections ?? [],
    svgTemplateId: config?.svgTemplateId ?? null,
    svgSections: config?.svgSections ?? [],
  };
}

function newSectionId() {
  return `sec-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

export function OverviewConfigSection({ deploymentId, canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { data: config, isLoading } = useOverviewConfig(deploymentId);
  const { data: allParams = [] } = useParameters(deploymentId);
  const saveConfig = useSaveOverviewConfig(deploymentId);
  const toast = useToast();
  const toastApiError2 = useApiErrorToast();

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const effective = draft ?? buildInitialOverview(config);
  const isDirty = draft !== null;

  function mutate(fn) {
    setDraft((prev) => {
      const base = prev ?? buildInitialOverview(config);
      return fn(structuredClone(base));
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveConfig.mutateAsync(effective);
      setDraft(null);
      toast({ message: 'Overview settings saved', type: 'success' });
    } catch (err) {
      toastApiError2(err);
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div style={{ padding: '16px 0', color: c.textSubtle, fontSize: typography.fontSize.md }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <CriticalParamsSection
        paramIds={effective.criticalParameterIds}
        allParams={allParams}
        canManage={canManage}
        onChange={(ids) => mutate((d) => { d.criticalParameterIds = ids; return d; })}
        c={c}
      />
      <SectionsConfig
        sections={effective.sections}
        allParams={allParams}
        canManage={canManage}
        onChange={(sections) => mutate((d) => { d.sections = sections; return d; })}
        c={c}
      />
      <DiagramConfigSection
        svgTemplateId={effective.svgTemplateId}
        svgSections={effective.svgSections}
        allParams={allParams}
        canManage={canManage}
        onTemplateChange={(id) => mutate((d) => { d.svgTemplateId = id; d.svgSections = []; return d; })}
        onSvgSectionsChange={(next) => mutate((d) => { d.svgSections = next; return d; })}
        c={c}
        isDark={isDark}
      />
      {isDirty && (
        <UnsavedBar
          count={null}
          label="Unsaved changes"
          saving={saving}
          onSave={handleSave}
          onDiscard={() => setDraft(null)}
        />
      )}
    </div>
  );
}

// ── ShiftsTab ─────────────────────────────────────────────────────────────────

function CriticalParamsSection({ paramIds, allParams, canManage, onChange, c }) {
  const [search, setSearch] = useState('');

  const selected = paramIds
    .map((id) => allParams.find((p) => p.id === id))
    .filter(Boolean);

  const available = allParams.filter(
    (p) => !paramIds.includes(p.id) &&
      p.displayName.toLowerCase().includes(search.toLowerCase())
  );

  function add(param) { onChange([...paramIds, param.id]); setSearch(''); }
  function remove(id) { onChange(paramIds.filter((x) => x !== id)); }
  function moveUp(idx) {
    if (idx === 0) return;
    const next = [...paramIds]; [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]; onChange(next);
  }
  function moveDown(idx) {
    if (idx === paramIds.length - 1) return;
    const next = [...paramIds]; [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]; onChange(next);
  }

  return (
    <section>
      <OvSectionHeader label="Critical Parameters" icon="warning" c={c} />
      <p style={{ fontSize: typography.fontSize.base, color: c.textSubtle, margin: '0 0 16px' }}>
        These appear prominently at the top of the Overview page.
      </p>
      {selected.length === 0 ? (
        <div style={{ marginBottom: 12 }}>
          <OvEmptyChip label="No critical parameters configured" c={c} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {selected.map((p, idx) => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', borderRadius: radius.md,
              background: c.bg, border: `1px solid ${c.borderSubtle}`, boxShadow: shadow.search,
            }}>
              <span style={{ flex: 1, fontSize: typography.fontSize.md, color: c.textPrimary, fontWeight: typography.fontWeight.medium }}>{p.displayName}</span>
              <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, marginRight: 4 }}>{p.unit ?? ''}</span>
              <OvParamTypeBadge type={p.type} />
              {canManage && (
                <>
                  <OvIconBtn icon="arrow_upward" title="Move up" disabled={idx === 0} onClick={() => moveUp(idx)} c={c} />
                  <OvIconBtn icon="arrow_downward" title="Move down" disabled={idx === selected.length - 1} onClick={() => moveDown(idx)} c={c} />
                  <OvIconBtn icon="close" title="Remove" danger onClick={() => remove(p.id)} c={c} />
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {canManage && (
        <OvParamSearchAdd search={search} onSearch={setSearch} results={available} onAdd={add} c={c} placeholder="Search parameters to mark as critical…" />
      )}
    </section>
  );
}

function SectionsConfig({ sections, allParams, canManage, onChange, c }) {
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  function addSection() {
    const name = newName.trim();
    if (!name) { setNameError('Name is required'); return; }
    if (sections.some((s) => s.name.toLowerCase() === name.toLowerCase())) { setNameError('Section name must be unique'); return; }
    const id = newSectionId();
    onChange([...sections, { id, name, order: sections.length + 1, parameterIds: [] }]);
    setNewName(''); setNameError(''); setExpandedId(id);
  }

  function deleteSection(id) { onChange(sections.filter((s) => s.id !== id)); if (expandedId === id) setExpandedId(null); }
  function renameSection(id, name) { onChange(sections.map((s) => s.id === id ? { ...s, name } : s)); }
  function moveSectionUp(idx) {
    if (idx === 0) return;
    const next = [...sections]; [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next.map((s, i) => ({ ...s, order: i + 1 })));
  }
  function moveSectionDown(idx) {
    if (idx === sections.length - 1) return;
    const next = [...sections]; [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next.map((s, i) => ({ ...s, order: i + 1 })));
  }
  function updateSectionParams(id, parameterIds) { onChange(sections.map((s) => s.id === id ? { ...s, parameterIds } : s)); }

  return (
    <section>
      <OvSectionHeader label="Overview Sections" icon="view_column" c={c} />
      <p style={{ fontSize: typography.fontSize.base, color: c.textSubtle, margin: '0 0 16px' }}>
        Each section appears as a tab on the Overview page with its assigned parameters.
      </p>
      {sections.length === 0 && <div style={{ marginBottom: 12 }}><OvEmptyChip label="No sections configured yet" c={c} /></div>}
      {sections.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {sections.map((sec, idx) => (
          <OvSectionCard
            key={sec.id} section={sec} idx={idx} total={sections.length}
            allParams={allParams} canManage={canManage}
            expanded={expandedId === sec.id}
            onToggle={() => setExpandedId(expandedId === sec.id ? null : sec.id)}
            onRename={(name) => renameSection(sec.id, name)}
            onDelete={() => deleteSection(sec.id)}
            onMoveUp={() => moveSectionUp(idx)}
            onMoveDown={() => moveSectionDown(idx)}
            onParamsChange={(ids) => updateSectionParams(sec.id, ids)}
            existingNames={sections.filter((s) => s.id !== sec.id).map((s) => s.name)}
            c={c}
          />
        ))}
      </div>}
      {canManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && addSection()}
              placeholder="New section name…"
              style={{
                flex: 1, padding: '8px 12px', borderRadius: radius.md, fontSize: typography.fontSize.md,
                fontFamily: typography.fontFamily.body,
                background: c.bg, color: c.textPrimary,
                border: `1.5px solid ${nameError ? semantic.errorBorder : c.borderSubtle}`, outline: 'none',
              }}
            />
            <PrimaryButton icon="add" onClick={addSection} size="sm">Add Section</PrimaryButton>
          </div>
          {nameError && <span style={{ fontSize: typography.fontSize.sm, color: semantic.error }}>{nameError}</span>}
        </div>
      )}
    </section>
  );
}


// ── Diagram (SVG/HMI) config ──────────────────────────────────────────────────

function DiagramConfigSection({ svgTemplateId, svgSections, allParams, canManage, onTemplateChange, onSvgSectionsChange, c, isDark }) {
  const { templates, usingFallback } = useSvgTemplates();
  const { isMobile, isTablet } = useBreakpoint();
  const isNarrow = isMobile || isTablet;
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const template = templates.find((t) => t.id === svgTemplateId) ?? null;
  const sectionList = template?.sections ?? [];

  // Client-side bulk assign: parse CSV, merge into the draft svgSections, and
  // surface counts/errors in the modal's result screen. No BE call — the merged
  // draft is persisted by the normal Save (PUT /overview-config).
  async function applyBulk(file) {
    const text = await file.text();
    const { svgSections: merged, success, failed, errors } = parseBulkSvgSections(
      text, allParams, sectionList, svgSections,
    );
    if (success > 0) onSvgSectionsChange(merged);
    if (success === 0 && failed > 0) {
      throw new Error(errors[0]?.message ?? 'No valid rows found.');
    }
    return { success, failed };
  }

  // Reset active region when template changes / clears.
  if (activeSectionId && !sectionList.some((s) => s.id === activeSectionId)) {
    setActiveSectionId(null);
  }

  function paramIdsFor(sectionId) {
    return svgSections.find((s) => s.sectionId === sectionId)?.parameterIds ?? [];
  }

  function setParamIdsFor(sectionId, ids) {
    const others = svgSections.filter((s) => s.sectionId !== sectionId);
    const next = ids.length > 0 ? [...others, { sectionId, parameterIds: ids }] : others;
    onSvgSectionsChange(next);
  }

  const boundCount = svgSections.filter((s) => (s.parameterIds?.length ?? 0) > 0).length;

  return (
    <section>
      <OvSectionHeader label="Diagram (HMI)" icon="schema" c={c} />
      <p style={{ fontSize: typography.fontSize.base, color: c.textSubtle, margin: '0 0 16px' }}>
        Pick an HMI diagram and assign parameters to each region. On the Overview page, hovering a region lists its parameters; clicking one opens its trend.
      </p>

      {/* Template picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textSubtle }}>Template</label>
        <div style={{ minWidth: 200, flex: isNarrow ? '1 1 100%' : '0 0 auto' }}>
          <AppSelect
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
            value={svgTemplateId ?? null}
            onChange={(v) => onTemplateChange(v || null)}
            size="sm"
            searchable={false}
            placeholder="— None —"
            disabled={!canManage}
          />
        </div>
        {usingFallback && (
          <span style={{ fontSize: typography.fontSize.sm, color: semantic.infoCyan, background: semantic.infoCyanBg, border: `1px solid ${semantic.infoCyanBorder}`, borderRadius: radius.sm, padding: '3px 8px' }}>
            Using bundled fallback templates
          </span>
        )}
        {template && (
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{boundCount}/{sectionList.length} regions assigned</span>
        )}
        {template && canManage && (
          <button
            onClick={() => setBulkOpen(true)}
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: radius.md, cursor: 'pointer',
              background: c.bg, color: c.textPrimary, border: `1px solid ${c.borderSubtle}`,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, fontFamily: typography.fontFamily.body,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>upload_file</span>
            Bulk assign
          </button>
        )}
      </div>

      {!template ? (
        <OvEmptyChip label="No diagram selected" c={c} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 16, alignItems: 'stretch' }}>
          {/* Live diagram preview */}
          <div style={{
            minHeight: isNarrow ? 240 : 320, borderRadius: radius.lg, border: `1px solid ${c.borderSubtle}`,
            background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle, padding: 12, overflow: 'hidden',
          }}>
            <SvgDiagram
              template={template}
              svgSections={svgSections}
              allParams={allParams}
              mode="config"
              activeSectionId={activeSectionId}
              onSectionClick={(id) => setActiveSectionId(id)}
            />
          </div>

          {/* Region list + per-region assignment */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
            {sectionList.map((sec) => (
              <DiagramRegionRow
                key={sec.id}
                section={sec}
                paramIds={paramIdsFor(sec.id)}
                allParams={allParams}
                canManage={canManage}
                expanded={activeSectionId === sec.id}
                onToggle={() => setActiveSectionId(activeSectionId === sec.id ? null : sec.id)}
                onChange={(ids) => setParamIdsFor(sec.id, ids)}
                c={c}
              />
            ))}
          </div>
        </div>
      )}

      {bulkOpen && template && (
        <BulkUploadModal
          title="Bulk Assign Parameters"
          subtitle={`Map regions of "${template.name}" to parameters. Merges into the current assignment; review and Save after.`}
          entityLabel="Assignment"
          requiredColumns={['section_id', 'parameter_data_key']}
          previewColumns={[
            { key: 'section_id', label: 'Region', flex: '1fr' },
            { key: 'parameter_data_key', label: 'Parameter (data key)', flex: '1.4fr' },
          ]}
          sampleCsv={buildSampleCsv(sectionList, allParams)}
          sampleFilename={`${template.id}-sections-sample.csv`}
          mutateAsync={applyBulk}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </section>
  );
}

function DiagramRegionRow({ section, paramIds, allParams, canManage, expanded, onToggle, onChange, c }) {
  const [search, setSearch] = useState('');

  const selected = paramIds.map((id) => allParams.find((p) => p.id === id)).filter(Boolean);
  const available = allParams.filter(
    (p) => !paramIds.includes(p.id) && p.displayName.toLowerCase().includes(search.toLowerCase())
  );

  function add(p) { onChange([...paramIds, p.id]); setSearch(''); }
  function remove(id) { onChange(paramIds.filter((x) => x !== id)); }

  return (
    <div style={{ borderRadius: radius.lg, border: `1px solid ${expanded ? semantic.brandBorderAccentMid : c.borderSubtle}`, background: c.bg, boxShadow: shadow.search }}>
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer' }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: expanded ? semantic.brandAccent : c.textSubtle }}>
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
        <span style={{ flex: 1, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{section.label}</span>
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, borderRadius: radius.lg, padding: '2px 8px',
          color: selected.length ? semantic.brandAccent : c.textMuted,
          background: selected.length ? semantic.brandBorderAccent : c.bgSubtle,
        }}>
          {selected.length}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 12px' }}>
          {selected.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {selected.map((p) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: radius.md,
                  background: c.bgSubtle, border: `1px solid ${c.borderSubtle}`,
                }}>
                  <span style={{ flex: 1, fontSize: typography.fontSize.md, color: c.textPrimary, fontWeight: typography.fontWeight.medium }}>{p.displayName}</span>
                  <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{p.unit ?? ''}</span>
                  {canManage && <OvIconBtn icon="close" title="Remove" danger small onClick={() => remove(p.id)} c={c} />}
                </div>
              ))}
            </div>
          )}
          {canManage && (
            <OvParamSearchAdd
              search={search} onSearch={setSearch} results={available} onAdd={add} c={c}
              placeholder="Search parameters to assign…"
            />
          )}
        </div>
      )}
    </div>
  );
}

function OvSectionCard({ section, idx, total, allParams, canManage, expanded, onToggle, onRename, onDelete, onMoveUp, onMoveDown, onParamsChange, existingNames, c }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(section.name);
  const [nameErr, setNameErr] = useState('');
  const [paramSearch, setParamSearch] = useState('');

  function commitRename() {
    const name = nameVal.trim();
    if (!name) { setNameErr('Required'); return; }
    if (existingNames.some((n) => n.toLowerCase() === name.toLowerCase())) { setNameErr('Name must be unique'); return; }
    onRename(name); setEditingName(false); setNameErr('');
  }

  const selectedParams = section.parameterIds.map((id) => allParams.find((p) => p.id === id)).filter(Boolean);
  const available = allParams.filter((p) => !section.parameterIds.includes(p.id) && p.displayName.toLowerCase().includes(paramSearch.toLowerCase()));
  function addParam(param) { onParamsChange([...section.parameterIds, param.id]); setParamSearch(''); }
  function removeParam(id) { onParamsChange(section.parameterIds.filter((x) => x !== id)); }
  function moveParamUp(i) {
    if (i === 0) return;
    const next = [...section.parameterIds]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; onParamsChange(next);
  }
  function moveParamDown(i) {
    if (i === section.parameterIds.length - 1) return;
    const next = [...section.parameterIds]; [next[i], next[i + 1]] = [next[i + 1], next[i]]; onParamsChange(next);
  }

  return (
    <div style={{ borderRadius: radius.lg, border: `1px solid ${c.borderSubtle}`, background: c.bg, boxShadow: shadow.search, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer',
        background: expanded ? c.bgSubtle : c.bg, borderBottom: expanded ? `1px solid ${c.divider}` : 'none',
      }} onClick={onToggle}>
        <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle, flexShrink: 0 }}>
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
        {editingName ? (
          <div style={{ flex: 1, display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <input autoFocus value={nameVal}
              onChange={(e) => { setNameVal(e.target.value); setNameErr(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingName(false); }}
              style={{
                flex: 1, padding: '4px 8px', borderRadius: radius.sm, fontSize: typography.fontSize.md,
                fontFamily: typography.fontFamily.body, background: c.bgPage, color: c.textPrimary,
                border: `1.5px solid ${nameErr ? semantic.errorBorder : c.borderInput}`, outline: 'none',
              }}
            />
            <OvIconBtn icon="check" title="Save" onClick={commitRename} c={c} />
            <OvIconBtn icon="close" title="Cancel" onClick={() => { setEditingName(false); setNameVal(section.name); }} c={c} />
          </div>
        ) : (
          <span style={{ flex: 1, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{section.name}</span>
        )}
        <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, marginRight: 4 }}>
          {section.parameterIds.length} param{section.parameterIds.length !== 1 ? 's' : ''}
        </span>
        {canManage && !editingName && (
          <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <OvIconBtn icon="edit" title="Rename" onClick={() => setEditingName(true)} c={c} />
            <OvIconBtn icon="arrow_upward" title="Move up" disabled={idx === 0} onClick={onMoveUp} c={c} />
            <OvIconBtn icon="arrow_downward" title="Move down" disabled={idx === total - 1} onClick={onMoveDown} c={c} />
            <OvIconBtn icon="delete" title="Delete section" danger onClick={onDelete} c={c} />
          </div>
        )}
      </div>
      {expanded && (
        <div style={{ padding: '14px' }}>
          {nameErr && <p style={{ margin: '0 0 8px', fontSize: typography.fontSize.sm, color: semantic.error }}>{nameErr}</p>}
          {selectedParams.length === 0 ? (
            <OvEmptyChip label="No parameters in this section" c={c} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
              {selectedParams.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: radius.md,
                  background: c.bgSubtle, border: `1px solid ${c.divider}`,
                }}>
                  <span style={{ flex: 1, fontSize: typography.fontSize.base, color: c.textPrimary }}>{p.displayName}</span>
                  <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{p.unit ?? ''}</span>
                  <OvParamTypeBadge type={p.type} small />
                  {canManage && (
                    <>
                      <OvIconBtn icon="arrow_upward" title="Move up" disabled={i === 0} onClick={() => moveParamUp(i)} c={c} small />
                      <OvIconBtn icon="arrow_downward" title="Move down" disabled={i === selectedParams.length - 1} onClick={() => moveParamDown(i)} c={c} small />
                      <OvIconBtn icon="close" title="Remove" danger onClick={() => removeParam(p.id)} c={c} small />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {canManage && (
            <OvParamSearchAdd search={paramSearch} onSearch={setParamSearch} results={available} onAdd={addParam} c={c} placeholder="Add parameter to section…" />
          )}
        </div>
      )}
    </div>
  );
}

function OvParamSearchAdd({ search, onSearch, results, onAdd, c, placeholder }) {
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const listRef = useRef(null);

  function handleKeyDown(e) {
    if (!search || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => {
        const next = Math.min(i + 1, results.length - 1);
        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => {
        const next = Math.max(i - 1, 0);
        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightIdx >= 0 ? highlightIdx : 0;
      if (results[idx]) { onAdd(results[idx]); setHighlightIdx(-1); }
    } else if (e.key === 'Escape') {
      onSearch('');
      setHighlightIdx(-1);
    }
  }

  return (
    <div style={{ position: 'relative' }} onKeyDown={handleKeyDown}>
      <SearchInput
        value={search}
        onChange={(val) => { onSearch(val); setHighlightIdx(-1); }}
        placeholder={placeholder}
        size="sm"
        containerStyle={{ width: '100%' }}
      />
      {search && results.length > 0 && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40,
          background: c.bg, border: `1px solid ${c.borderSubtle}`, borderRadius: radius.md,
          boxShadow: shadow.panel, maxHeight: 200, overflowY: 'auto', marginTop: 2,
        }}>
          {results.map((p, i) => (
            <div key={p.id} onClick={() => { onAdd(p); setHighlightIdx(-1); }}
              onMouseEnter={() => setHighlightIdx(i)}
              onMouseLeave={() => setHighlightIdx(-1)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                cursor: 'pointer', fontSize: typography.fontSize.base, color: c.textPrimary,
                background: highlightIdx === i ? c.bgHover : 'transparent',
              }}
            >
              <span style={{ flex: 1 }}>{p.displayName}</span>
              <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{p.unit ?? ''}</span>
              <OvParamTypeBadge type={p.type} small />
            </div>
          ))}
        </div>
      )}
      {search && results.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40,
          background: c.bg, border: `1px solid ${c.borderSubtle}`, borderRadius: radius.md,
          padding: '10px 12px', fontSize: typography.fontSize.base, color: c.textSubtle, marginTop: 2,
        }}>
          No matching parameters
        </div>
      )}
    </div>
  );
}

function OvSectionHeader({ label, icon, c }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${c.border}` }}>
      <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle }}>{icon}</span>
      <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body }}>{label}</span>
    </div>
  );
}

function OvEmptyChip({ label, c }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: radius.md,
      background: c.bgSubtle, border: `1px solid ${c.divider}`,
      fontSize: typography.fontSize.base, color: c.textSubtle, fontStyle: 'italic',
    }}>
      {label}
    </div>
  );
}

function OvIconBtn({ icon, title, onClick, danger, disabled, c, small }) {
  const sz = small ? 24 : 28;
  return (
    <button title={title} disabled={disabled} onClick={onClick}
      style={{
        width: sz, height: sz, borderRadius: radius.sm, padding: 0,
        border: `1px solid ${danger ? semantic.errorBorder : c.borderSubtle}`,
        background: c.bg, cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.35 : 1,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: small ? 12 : 13, color: danger ? semantic.error : c.textMuted }}>
        {icon}
      </span>
    </button>
  );
}

const OV_PARAM_TYPE_COLOR = {
  target:       { bg: 'rgba(30,67,150,0.08)',  text: '#1e4396',  label: 'T' },
  controllable: { bg: 'rgba(16,185,129,0.08)', text: '#059669',  label: 'C' },
  observed:     { bg: 'rgba(245,158,11,0.08)', text: '#d97706',  label: 'O' },
  monitored:    { bg: 'rgba(139,92,246,0.08)', text: '#7c3aed',  label: 'M' },
};

function OvParamTypeBadge({ type, small }) {
  const meta = OV_PARAM_TYPE_COLOR[type] ?? OV_PARAM_TYPE_COLOR.observed;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: small ? 16 : 18, height: small ? 16 : 18, borderRadius: radius.xs,
      background: meta.bg, color: meta.text,
      fontSize: small ? 9 : 10, fontWeight: typography.fontWeight.bold, flexShrink: 0,
    }}>
      {meta.label}
    </span>
  );
}
